// ============================================
// Chat Module - User Note (ES6)
// 유저 노트 관리
// ============================================

import { CHAT_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import { getCurrentRoomId, getCurrentSessionId, getCurrentUser } from '../app/state.js';
import { showToast, escapeHtml, showGlobalLoading, hideGlobalLoading } from '../utils.js';

// ============================================
// 캐시
// ============================================

let userNoteCache = {
  data: null,
  sessionId: null
};

export function clearUserNoteCache() {
  userNoteCache.data = null;
  userNoteCache.sessionId = null;
}

// ============================================
// 유저 노트 렌더링
// ============================================

export async function renderUserNoteTab() {
  const content = document.getElementById('memoryContent');
  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();

  // 세션 구조만 지원 (레거시 chats 컬렉션 제거됨)
  if (!roomId || !sessionId) {
    content.innerHTML = '<div style="color: var(--text-secondary); text-align: center; padding: 20px;">세션을 선택하세요</div>';
    return;
  }

  let userNote = '';

  // 캐시 확인
  if (userNoteCache.data !== null && userNoteCache.sessionId === sessionId) {
    userNote = userNoteCache.data;
  } else {
    // 로딩 표시 (정적 HTML)
    content.innerHTML = '<div style="display: flex; justify-content: center; padding: 40px;"><div class="spinner"></div></div>';

    // Firestore 직접 연결로 유저 노트 로드
    const db = firebase.firestore();

    try {
      const docRef = db.collection('users').doc(getCurrentUser()?.uid)
        .collection('chatRooms').doc(roomId)
        .collection('sessions').doc(sessionId);

      const chatDoc = await docRef.get();
      if (chatDoc.exists) {
        userNote = chatDoc.data().userNote || '';
      }

      // 캐시에 저장
      userNoteCache.data = userNote;
      userNoteCache.sessionId = sessionId;
    } catch (e) {
      console.error('유저 노트 로드 오류:', e);
    }
  }

  content.innerHTML = `
    <!-- User Note Section -->
    <div class="user-note-section" style="border-top: none; margin-top: 0; padding-top: 0;">
      <div class="user-note-header">
        <h4>유저 노트</h4>
      </div>
      <p style="font-size: 12px; color: var(--text-secondary); margin-bottom: 12px;">
        AI가 절대 잊지 않았으면 하는 내용을 적어주세요.<br>
        이 내용은 항상 AI에게 전달됩니다.
      </p>
      <textarea
        class="user-note-textarea"
        id="userNoteTextarea"
        placeholder="예: 나는 고양이 알러지가 있어, 내 여자친구 이름은 수진이야..."
        maxlength="2000"
        oninput="updateCharCount('userNoteTextarea', 'userNoteCount', 2000)"
      >${escapeHtml(userNote)}</textarea>
      <div class="char-count" id="userNoteCount">${userNote.length} / 2000</div>
      <button class="btn btn-primary" style="margin-top: 12px;" data-action="saveUserNote">
        저장
      </button>
    </div>
  `;

  // 이벤트 위임
  content.querySelector('[data-action="saveUserNote"]')?.addEventListener('click', saveUserNote);
}

// ============================================
// 유저 노트 저장
// ============================================

export async function saveUserNote() {
  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();

  // 세션 구조만 지원
  if (!roomId || !sessionId) {
    showToast('세션을 선택해주세요.', true);
    return;
  }

  const textarea = document.getElementById('userNoteTextarea');
  if (!textarea) {
    showToast('입력란을 찾을 수 없습니다.', true);
    return;
  }

  const note = textarea.value.trim();

  if (note.length > 2000) {
    showToast('유저 노트는 2000자를 초과할 수 없습니다.', true);
    return;
  }

  try {
    showGlobalLoading('저장 중...');

    // 세션 구조만 지원
    const response = await authFetch(CHAT_ENDPOINTS.saveUserNote, {
      body: {
        userNote: note,
        roomId,
        sessionId
      }
    });

    hideGlobalLoading();

    if (!response.ok) throw new Error('저장 실패');

    // 캐시 업데이트
    userNoteCache.data = note;
    userNoteCache.sessionId = sessionId;

    showToast('유저 노트가 저장되었습니다');
  } catch (e) {
    hideGlobalLoading();
    showToast('저장 오류: ' + e.message, true);
  }
}

// ============================================
// 글자 수 카운터
// ============================================

export function updateCharCount(inputId, countId, max) {
  const input = document.getElementById(inputId);
  const count = document.getElementById(countId);
  const len = input.value.length;

  count.textContent = `${len} / ${max}`;
  count.className = 'char-count';

  if (len >= max) {
    count.classList.add('error');
  } else if (len >= max * 0.8) {
    count.classList.add('warning');
  }
}

// ============================================
// 모달 닫기 유틸리티
// ============================================

export function closeModal(modalId) {
  const modal = document.getElementById(modalId);
  if (!modal) return;

  const staticModals = ['worldInfoModal', 'detailMenuModal'];

  if (staticModals.includes(modalId)) {
    modal.style.display = 'none';
  } else {
    modal.classList.remove('open');
    setTimeout(() => {
      modal.remove();
      if (window.ModalManager) {
        window.ModalManager.unregister(modalId);
      }
    }, 300);
  }

  if (window.ModalManager) {
    window.ModalManager.remove(modalId, true);
  }
}

// ============================================
// window 노출 제거됨 (2026-01-06)
// 모든 호출이 ES6 import로 처리됨
// ============================================
